export type Ease = (t: number) => number;

export const ease = {
  linear: (t: number) => t,
  inCubic: (t: number) => t * t * t,
  outCubic: (t: number) => 1 - (1 - t) ** 3,
  inOutCubic: (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2),
  outExpo: (t: number) => (t >= 1 ? 1 : 1 - 2 ** (-10 * t)),
  /** Short overshoot: a card pushed onto a pin, not a jelly wobble. */
  outBack: (t: number) => {
    const c = 1.5;
    return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2;
  },
} satisfies Record<string, Ease>;

interface Tween {
  start: number;
  duration: number;
  ease: Ease;
  update: (t: number) => void;
  resolve: () => void;
}

/** Time-based tweens stepped by the render loop; `finishAll` snaps everything to its end state. */
export class Animator {
  private readonly tweens = new Set<Tween>();

  /** `wake` restarts an idle render loop: a tween chained after another would never be stepped. */
  constructor(private readonly wake: () => void = () => {}) {}

  tween(duration: number, update: (t: number) => void, options: { ease?: Ease; delay?: number } = {}): Promise<void> {
    return new Promise((resolve) => {
      this.tweens.add({
        start: performance.now() + (options.delay ?? 0),
        duration: Math.max(1, duration),
        ease: options.ease ?? ease.outCubic,
        update,
        resolve,
      });
      this.wake();
    });
  }

  wait(ms: number): Promise<void> {
    return this.tween(ms, () => {}, { ease: ease.linear });
  }

  step(now: number): void {
    for (const tween of this.tweens) {
      if (now < tween.start) continue;
      const progress = Math.min(1, (now - tween.start) / tween.duration);
      tween.update(tween.ease(progress));
      if (progress >= 1) {
        this.tweens.delete(tween);
        tween.resolve();
      }
    }
  }

  finishAll(): void {
    for (const tween of this.tweens) {
      tween.update(tween.ease(1));
      tween.resolve();
    }
    this.tweens.clear();
  }

  get busy(): boolean {
    return this.tweens.size > 0;
  }
}
